/**
 * The four catalog forms' one anatomy — Board 1 (`docs/catalog-units-and-buying.md` §6).
 *
 * Materials, Labor, Equipment (hired in) and Fees are one form with four sets
 * of words: the same five sections in the same order, the same two pairs and
 * green sentence, the same Readout, the same Where and Options. These are the
 * parts every one of them is assembled from, so a fifth form — or a change to
 * one section — is made in one place and the four cannot drift apart.
 *
 * Props only. Every number is worked out by `shared/src/buying.ts`, which the
 * server prices lines with too; nothing here does arithmetic of its own beyond
 * reading a typed box.
 */
import { type ReactNode } from 'react'
import { CUSTOMER_NAME_MAX, tryIt, type BuyingKind, type BuyingRule } from '@rowted/shared'
import type { OrgUnitSummary } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { Button } from '../primitives.js'
import {
  BranchScopeField, CheckRow, Field, SegmentedField, SelectInput, TextArea, TextInput, Wide,
} from '../form/index.js'
import { Sheet } from '../sheet.js'
import type { TileKind } from '../../builder/kind-tile.js'
import { EM_DASH, formatNumber, formatRate } from '../../lib/format.js'
import { IconLock } from '../../frame/icons.js'
import {
  A_FIXED_PRICE, BUYS, CUSTOMER_NAME, CUSTOMER_NAME_HINT, KIND_SUB, COSTS_THE_JOB,
  EVERYWHERE_HINT, ITS_OWN_MARKUP, MARGIN,
  NOTES, OPTIONS, PRICED_BY, SAVE, SAVE_AND_ADD_ANOTHER, SELLS, TAXABLE, TRY_IT,
  WHERE_IT_CAN_BE_USED, companyMarkup, type CatalogKind,
  COSTS_THE_JOB_LOWER, MARGIN_LOWER, ONE_SETTING, costedTwoWays, ruleUsed, ruleWhole,
} from './catalog-copy.js'

/** A typed box read as a number; empty and nonsense are null, never NaN. */
export const readAmount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * What a box shows for a stored number — `486.50`, not the `486.500000` a
 * numeric(14,6) arrives as; a money box keeps two places, a count none it does
 * not need.
 */
export const amountText = (value: unknown, money = false): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return money && Math.abs(n * 100 - Math.round(n * 100)) < 1e-9 ? n.toFixed(2) : String(n)
}

/** `25.7%`, or the dash when there is no price to measure against. */
export const percentText = (pct: number | null) =>
  pct === null ? EM_DASH : `${formatNumber(pct)}%`

/** Accent at 20% and over, danger below — the Readout's and Try it's one rule. */
export const marginTone = (pct: number | null): 'default' | 'accent' | 'danger' =>
  pct === null ? 'default' : pct >= 20 ? 'accent' : 'danger'

// ---------------------------------------------------------------------------
// The shell
// ---------------------------------------------------------------------------

/**
 * `CatalogSheet` — the product's one record flyout (`components/sheet.tsx`):
 * 620px from the right over a scrim, the kind's tile beside the name and the
 * kind, Cancel · Save & add another · Save at the foot, and a full-screen
 * form on a phone.
 *
 * It was a 480 box in the middle of the screen (Board 18), which made the
 * catalog the one place in the app where creating a record did not look or
 * behave like creating one anywhere else — Aaron, step 21 item 1: "same
 * format, same function, just in a right hand popout". So it is the `Sheet`
 * the contact, job and property forms wear, and closing with changes asks the
 * same three-answer question theirs does.
 */
export function CatalogSheet({
  kind, tile, title, dirty, changeCount, busy, canEdit, isNew, onClose, onSave, note, layer, children,
  sub,
}: {
  /** `Sheet`'s layer: 50 over the page, higher when opened from a picker. */
  layer?: number
  kind: CatalogKind
  /** The header's second line when the kind's own does not say it (an owned machine). */
  sub?: string
  tile: TileKind
  title: string
  dirty: boolean
  changeCount: number
  busy: boolean
  canEdit: boolean
  isNew: boolean
  onClose: () => void
  onSave: (again: boolean) => void
  /** Why Save did not go — "One field needs attention". */
  note?: ReactNode
  children: ReactNode
}) {
  return (
    <Sheet
      open
      onClose={onClose}
      kind={tile}
      title={title}
      sub={sub ?? KIND_SUB[kind]}
      layer={layer}
      dirty={canEdit && dirty}
      changeCount={changeCount}
      saveLabel={SAVE}
      onSave={canEdit ? () => onSave(false) : undefined}
      note={note ? <span className="min-w-0 truncate text-[12px] text-danger">{note}</span> : undefined}
      secondary={isNew && canEdit
        ? (
          <Button className="h-[34px]" disabled={busy} onClick={() => onSave(true)}>
            {SAVE_AND_ADD_ANOTHER}
          </Button>
        )
        : undefined}
      primary={canEdit
        ? (
          <Button variant="primary" className="h-[34px]" disabled={busy} onClick={() => onSave(false)}>
            {busy ? 'Saving…' : SAVE}
          </Button>
        )
        : null}
    >
      <div data-catalog-sheet={kind} className="flex flex-col gap-3.5">{children}</div>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export interface UnitOption { id: string; code: string; name: string; kind: string }

/**
 * Units grouped by what they measure — a heading where the kind changes, the
 * way `<optgroup>` would have, because a flat list of forty is a wall.
 */
const UNIT_GROUPS: { kind: string; label: string }[] = [
  { kind: 'count', label: 'Count' },
  { kind: 'pack', label: 'Packaging' },
  { kind: 'area', label: 'Area' },
  { kind: 'length', label: 'Length' },
  { kind: 'volume', label: 'Volume' },
  { kind: 'weight', label: 'Weight' },
  { kind: 'time', label: 'Time' },
]

export function unitOptions(units: UnitOption[]) {
  const known = new Set(UNIT_GROUPS.map((group) => group.kind))
  return [
    ...UNIT_GROUPS.flatMap((group) => units
      .filter((unit) => unit.kind === group.kind)
      .map((unit) => ({ unit, group: group.label }))),
    // A kind nobody has named yet still has to be choosable.
    ...units.filter((unit) => !known.has(unit.kind)).map((unit) => ({ unit, group: 'Other' })),
  ].map(({ unit, group }) => ({ value: unit.id, label: unit.name, group }))
}

/**
 * **No typed units anywhere** (§6, do-not-substitute 2). Bought by, Paid by,
 * Hired by and Allocated in are all this: a menu over the Units table, a
 * sheet on a phone, its last row making a new one from what was typed.
 */
export function UnitSelect({
  label, value, units, onChange, onCreate, invalid, disabled, id, describedBy,
}: {
  label: string
  value: string | null
  units: UnitOption[]
  onChange: (id: string | null) => void
  /** Makes a unit named after what was typed; the select then picks it. */
  onCreate?: (name: string) => void
  invalid?: boolean
  disabled?: boolean
  id?: string
  describedBy?: string
}) {
  return (
    <SelectInput
      id={id}
      describedBy={describedBy}
      label={label}
      value={value}
      invalid={invalid}
      disabled={disabled}
      onChange={onChange}
      options={unitOptions(units)}
      onCreate={onCreate}
      createThing="unit"
    />
  )
}

/** What a unit is called in a sentence — its name, or a fallback while none is chosen. */
export function unitNamed(units: UnitOption[], id: string | null, fallback: string) {
  const unit = units.find((one) => one.id === id)
  return { code: unit?.code ?? fallback, name: unit?.name ?? fallback }
}

// ---------------------------------------------------------------------------
// Try it
// ---------------------------------------------------------------------------

/**
 * The Try it panel — **internal**: the tinted surface, the lock, never white
 * (do-not-substitute 5). On Materials, Equipment and Fees; never on Labor,
 * which is costed on its hours. Every figure is `tryIt()`'s.
 */
export function TryItPanel({
  rule, sellsEach, kind, qty, onQty, allocWord, compare,
}: {
  rule: BuyingRule
  sellsEach: number
  kind: BuyingKind
  qty: string
  onQty: (text: string) => void
  allocWord: string
  /** Board 2's “One setting” under the panel — drawn for a material only. */
  compare?: boolean
}) {
  const amount = readAmount(qty) ?? 0
  const result = tryIt(rule, amount, sellsEach, kind)
  return (
    <>
    <div data-try-it="" className="rounded-[10px] border border-line-faint bg-surface-internal px-3 pb-[11px] pt-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-bold text-text">{TRY_IT}</span>
        <input
          aria-label={TRY_IT}
          value={qty}
          inputMode="decimal"
          onChange={(event) => onQty(event.target.value)}
          className={cn('tnum h-9 w-[84px] rounded-field border border-line bg-surface px-[11px]',
            'text-[13.5px] font-bold text-text focus:border-accent-line focus:outline-none')}
        />
        <span className="text-[12.5px] text-text-muted">{allocWord}</span>
        <span aria-hidden className="text-text-faint">→</span>
        <span className="text-[12.5px] text-text">
          {BUYS} <b className="tnum font-bold">{result.bought}</b>
          {result.over && <> · {result.over}</>}
        </span>
        <span aria-hidden className="ml-auto text-text-faint"><IconLock size={10} /></span>
      </div>
      <div className="mt-[9px] flex gap-[22px] text-[11.5px] text-text-muted">
        <span>{COSTS_THE_JOB}<br /><b className="tnum text-[14px] text-text">{formatRate(result.costs)}</b></span>
        <span>{SELLS}<br /><b className="tnum text-[14px] text-text">{formatRate(result.sells)}</b></span>
        <span>
          {MARGIN}<br />
          <b className={cn('tnum text-[14px]', marginTone(result.marginPct) === 'danger'
            ? 'text-danger' : 'text-accent')}>
            {percentText(result.marginPct)}
          </b>
        </span>
      </div>
      {result.note && (
        <p className="m-0 mt-2 text-[11.5px] leading-[1.4] text-text-muted">{result.note}</p>
      )}
    </div>
    {compare && rule.per !== 1 && amount > 0 && (
      <TwoWays rule={rule} sellsEach={sellsEach} kind={kind} amount={amount} allocWord={allocWord} />
    )}
    </>
  )
}

/**
 * **One setting** (Board 2, contract §8): the same quantity costed both ways
 * the rule allows, side by side, the rule's own answer selected. It is the
 * Try it arithmetic run twice — `tryIt` with each basis — so it can never
 * disagree with the panel above it, and it is a reading rather than a second
 * control: the segmented rule is still where the setting changes.
 */
function TwoWays({
  rule, sellsEach, kind, amount, allocWord,
}: { rule: BuyingRule; sellsEach: number; kind: BuyingKind; amount: number; allocWord: string }) {
  const ways = [
    { basis: 'used' as const, label: ruleUsed(kind, allocWord) },
    { basis: 'whole' as const, label: ruleWhole(rule.buyUnitName) },
  ]
  return (
    <div data-two-ways="" className="flex flex-col gap-2">
      <p className="m-0 text-[13px] text-text-muted">
        <b className="font-bold text-text">{ONE_SETTING}</b>{' '}
        {costedTwoWays(`${formatNumber(amount)} ${allocWord}`)}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {ways.map((way) => {
          const result = tryIt({ ...rule, basis: way.basis }, amount, sellsEach, kind)
          const on = rule.basis === way.basis
          return (
            <div key={way.basis} data-way={way.basis} aria-current={on || undefined}
              className={cn('rounded-[10px] border px-3 py-2.5 text-[12px] text-text-muted',
                on ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface')}>
              <b className="block text-[13px] font-bold text-text">{way.label}</b>
              <span className="block">
                {COSTS_THE_JOB_LOWER} <b className="tnum font-bold text-text">{formatRate(result.costs)}</b>
              </span>
              <span className="block">
                {MARGIN_LOWER}{' '}
                <b className={cn('tnum font-bold', marginTone(result.marginPct) === 'danger'
                  ? 'text-danger' : 'text-accent')}>
                  {percentText(result.marginPct)}
                </b>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// What it sells for
// ---------------------------------------------------------------------------

export type PriceMode = 'company' | 'markup' | 'fixed'

/**
 * Priced by — a segmented control on a desktop, a select sheet with the full
 * labels on a phone, because three segments with these words do not fit
 * 358px (§6.4).
 */
export function PricedByField({
  value, onChange, companyPct, disabled,
}: {
  value: PriceMode
  onChange: (mode: PriceMode) => void
  /** Given only on Materials — the one kind a company markup applies to. */
  companyPct?: number
  disabled?: boolean
}) {
  const phone = usePhone()
  const options = [
    ...(companyPct !== undefined
      ? [{ value: 'company' as const, label: companyMarkup(companyPct) }] : []),
    { value: 'markup' as const, label: ITS_OWN_MARKUP },
    { value: 'fixed' as const, label: A_FIXED_PRICE },
  ]
  return (
    <Field group={!phone} label={PRICED_BY}>
      {({ labelId, id }) => (phone
        ? (
          <SelectInput
            id={id}
            label={PRICED_BY}
            value={value}
            disabled={disabled}
            onChange={(next) => onChange((next as PriceMode) ?? value)}
            options={options}
          />
        )
        : <SegmentedPicker labelledBy={labelId} value={value} onChange={onChange} options={options} />)}
    </Field>
  )
}

/** The kit's segmented control, for the two-way and three-way choices on these forms. */
export function SegmentedPicker<T extends string>({
  labelledBy, value, onChange, options, label = PRICED_BY,
}: {
  labelledBy?: string
  value: T
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  label?: string
}) {
  return (
    <SegmentedField labelledBy={labelledBy} label={label} value={value} onChange={onChange}
      options={options} />
  )
}

// ---------------------------------------------------------------------------
// Where it can be used, Options, Notes
// ---------------------------------------------------------------------------

/**
 * Two columns: where it may be used (the branch scope the service pop-out
 * wears, reading **Everywhere** when nothing is ticked) and Options — Taxable
 * on all four kinds, plus whatever a kind adds under it.
 */
export function WhereAndOptions({
  available, orgUnitIds, onOrgUnits, taxable, onTaxable, disabled, extra,
}: {
  available: OrgUnitSummary[]
  orgUnitIds: string[]
  onOrgUnits: (next: string[]) => void
  taxable: boolean
  onTaxable: (next: boolean) => void
  disabled?: boolean
  extra?: ReactNode
}) {
  return (
    <div className="grid grid-cols-1 gap-x-3.5 gap-y-3 sm:grid-cols-2">
      <Field label={WHERE_IT_CAN_BE_USED} hint={EVERYWHERE_HINT}>
        {({ id }) => (
          <BranchScopeField id={id} available={available} value={orgUnitIds}
            onChange={onOrgUnits} disabled={disabled} layer={60} />
        )}
      </Field>
      <div className="flex min-w-0 flex-col gap-[5px]">
        <span className="text-fieldlabel text-text-muted">{OPTIONS}</span>
        <CheckRow label={TAXABLE} checked={taxable} disabled={disabled} onChange={onTaxable} />
        {extra}
      </div>
    </div>
  )
}

export function NotesField({ value, onChange, disabled }: {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
}) {
  return (
    <Field label={NOTES} optional>
      {({ id }) => (
        <TextArea id={id} rows={2} value={value} disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.value)} />
      )}
    </Field>
  )
}

/**
 * **Customer-facing name** (Aaron, step 24; migration 0126) — the full-width
 * row directly under Name on every form a quote line can name.
 *
 * The placeholder is the Name as it is typed now, so an empty box says what a
 * customer will read rather than looking like a question nobody answered; the
 * form sends a blank as null, which is what "the same as the name" is stored
 * as. One part, so the five forms cannot word it five ways.
 */
export function CustomerNameField({ value, name, onChange, disabled }: {
  value: string
  /** The record's Name — what a blank box prints, and so its placeholder. */
  name: string
  onChange: (next: string) => void
  disabled?: boolean
}) {
  return (
    <Wide>
      <Field label={CUSTOMER_NAME} hint={CUSTOMER_NAME_HINT}>
        {({ id, describedBy }) => (
          <TextInput id={id} describedBy={describedBy} value={value} placeholder={name}
            maxLength={CUSTOMER_NAME_MAX} disabled={disabled}
            onChange={(event) => onChange(event.target.value)} />
        )}
      </Field>
    </Wide>
  )
}
